import { z } from 'zod';
import { LEAVE_TYPES, OT_TYPES, ROLES } from '../types/index.js';

// Re-export integration schemas (JobPilot ↔ PayPilot — see HANDOFF-CONTRACT.md)
export * from './integrations.js';

// === Reusable primitives ===
export const dateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'รูปแบบวันที่ต้องเป็น YYYY-MM-DD');
export const timeString = z.string().regex(/^\d{2}:\d{2}$/, 'รูปแบบเวลาต้องเป็น HH:mm');
export const periodString = z.string().regex(/^\d{4}-\d{2}$/, 'รูปแบบงวดต้องเป็น YYYY-MM');
export const empCodeString = z.string().min(1).max(20);

// === Auth ===
export const meRequestSchema = z.object({
  idToken: z.string().min(10),
});
export type MeRequest = z.infer<typeof meRequestSchema>;

export const pairRequestSchema = z.object({
  idToken: z.string().min(10),
  // Public tenant code (e.g. STHKJU) — disambiguates pair codes across the
  // shared LINE channel. Auto-generated codes are 6 chars, but Owners can pick
  // a vanity code of 4–12 (ดู TENANT_CODE_PATTERN / signupOwnerSchema) — so this
  // MUST accept the same range, not a hard .length(6), or those tenants' staff
  // can't pair ("validation failed" at the final step). resolved by DB lookup.
  tenant_code: z.string().regex(/^[A-Z0-9]{4,12}$/i, 'รหัสบริษัทไม่ถูกต้อง'),
  code: z.string().length(6),
  // Identity proof captured during pairing so we can attribute future
  // actions to the right person even if the LINE userId changes.
  selfie_path: z.string().min(1),
  national_id_path: z.string().optional(),
});
export type PairRequest = z.infer<typeof pairRequestSchema>;

/** Allowed tenant_code character set + length range (used by both
 *  client-side suggest UI and server-side enforcement). */
export const TENANT_CODE_PATTERN = /^[A-Z0-9]{4,12}$/;

/** Codes the user can't claim — admin / billing / brand-protection words. */
export const TENANT_CODE_RESERVED = new Set([
  'ADMIN', 'ROOT', 'OWNER', 'SYSTEM', 'PAYROLL', 'SUPPORT', 'HELP',
  'TEST', 'DEMO', 'EXAMPLE', 'NULL', 'UNDEFINED', 'STAFF', 'HR',
  'BOSS', 'BILLING', 'INVOICE', 'API', 'STAGING', 'PROD',
  'PAYROLLEDGE', 'EDGE', 'MOODATA',
]);

export const signupOwnerSchema = z.object({
  // สมัครด้วยอีเมล+รหัสผ่าน (ไม่ผ่าน LINE) — LINE เป็นการผูกทีหลังแบบเลือกได้
  email: z.string().email('รูปแบบอีเมลไม่ถูกต้อง').max(254),
  password: z.string().min(8, 'รหัสผ่านอย่างน้อย 8 ตัวอักษร').max(200),
  company_name: z.string().min(2).max(100),
  owner_name: z.string().min(2).max(100),
  phone: z.string().max(20).optional(),
  /** Optional vanity code. If omitted, server auto-generates. */
  tenant_code: z
    .string()
    .regex(TENANT_CODE_PATTERN, 'รหัสต้องเป็น A-Z / 0-9 ความยาว 4-12 ตัว')
    .optional(),
});
export type SignupOwnerRequest = z.infer<typeof signupOwnerSchema>;

// === Web login (email + password, no LINE) ===
//
// แชร์ระหว่าง paypilot/jobpilot/evalpilot (อาศัย web_identities table ใน
// Supabase ตัวเดียวกัน). ปกติเซ็ตรหัสครั้งแรกจาก LIFF (Owner → ตั้งค่า
// → เปิด login เว็บ) แล้วใช้ /login เข้าจาก desktop ได้.
export const webLoginSchema = z.object({
  email: z.string().email('รูปแบบอีเมลไม่ถูกต้อง').max(254),
  password: z.string().min(8, 'รหัสผ่านอย่างน้อย 8 ตัวอักษร').max(200),
});
export type WebLoginRequest = z.infer<typeof webLoginSchema>;

export const webSetPasswordSchema = z.object({
  email: z.string().email('รูปแบบอีเมลไม่ถูกต้อง').max(254),
  password: z.string().min(8, 'รหัสผ่านอย่างน้อย 8 ตัวอักษร').max(200),
});
export type WebSetPasswordRequest = z.infer<typeof webSetPasswordSchema>;

// === Leave ===
export const LEAVE_PERIODS = ['full', 'morning', 'afternoon', 'custom'] as const;
export type LeavePeriod = (typeof LEAVE_PERIODS)[number];

export const submitLeaveSchema = z
  .object({
    leave_type: z.enum(LEAVE_TYPES as readonly [string, ...string[]]),
    dates: z.array(dateString).min(1).max(31),
    reason: z.string().max(500).optional(),
    evidence_path: z.string().optional(),
    is_emergency: z.boolean().optional(),
    period: z.enum(LEAVE_PERIODS).optional().default('full'),
    start_time: timeString.optional(),
    end_time: timeString.optional(),
  })
  .superRefine((v, ctx) => {
    if (v.period === 'custom') {
      if (!v.start_time || !v.end_time) {
        ctx.addIssue({
          code: 'custom',
          message: 'custom period requires start_time + end_time',
          path: ['period'],
        });
      } else if (v.end_time <= v.start_time) {
        ctx.addIssue({
          code: 'custom',
          message: 'เวลาสิ้นสุดต้องอยู่หลังเวลาเริ่ม',
          path: ['end_time'],
        });
      }
    }
    // half-days only make sense on single-day leaves
    if ((v.period === 'morning' || v.period === 'afternoon' || v.period === 'custom') && v.dates.length > 1) {
      ctx.addIssue({
        code: 'custom',
        message: 'half-day / custom period only allowed on single-day leaves',
        path: ['period'],
      });
    }
    // Personal leave (ลากิจ) requires a substantial reason — approvers
    // can otherwise send it back as info_requested anyway.
    if (v.leave_type === 'personal') {
      const r = (v.reason ?? '').trim();
      if (r.length < 10) {
        ctx.addIssue({
          code: 'custom',
          message: 'ลากิจต้องระบุเหตุผลและความจำเป็น (อย่างน้อย 10 ตัวอักษร)',
          path: ['reason'],
        });
      }
    }
  });
export type SubmitLeaveRequest = z.infer<typeof submitLeaveSchema>;

/** ชั่วโมงทำงานมาตรฐานต่อวัน — ตัวหารเดียวสำหรับแปลง ชั่วโมง↔วัน ทั้งระบบ
 *  (ตอนยื่นลา, ตอน reconcile หักเงิน, และ UI). 8 ชม. = 1 วัน (กฎแรงงานไทย). */
export const HOURS_PER_DAY = 8;
export const FULL_DAY_MINUTES = HOURS_PER_DAY * 60; // 480

/**
 * Fraction of a working day this period consumes from leave_quota.
 * - custom (ช่วงเวลา) หารด้วยชั่วโมงทำงานจริงต่อวันของพนักงาน (`hoursPerDay`,
 *   default 8) → พนักงานกะ 6 ชม. ลาทั้งวัน = 1.0 ไม่ใช่ 0.75.
 * - ปัด 3 ตำแหน่ง: 1 ชม. = 0.125 พอดี (2 ตำแหน่งจะได้ 0.13 → เอนเอียง +4% เสียเปรียบ
 *   พนักงาน). ต้องเรียกด้วย `hoursPerDay` เดียวกันทั้งตอนยื่นลาและตอน reconcile.
 */
export function leavePeriodDays(
  period: LeavePeriod,
  start_time?: string,
  end_time?: string,
  hoursPerDay: number = HOURS_PER_DAY,
): number {
  if (period === 'full') return 1;
  // fb#345 — ครึ่งวันที่ยื่นหลังแก้จะเก็บเวลาตามกะไว้ (เช้า 09–12 = 3 ชม. · บ่าย 13–18 = 5 ชม.)
  // ใบเก่าที่ไม่มีเวลา = 0.5 เหมือนเดิม
  if ((period === 'morning' || period === 'afternoon') && !(start_time && end_time)) return 0.5;
  if (start_time && end_time) {
    const perDay = (hoursPerDay > 0 ? hoursPerDay : HOURS_PER_DAY) * 60;
    return Math.max(0, Math.round((leaveMinutes(start_time, end_time) / perDay) * 1000) / 1000); // 3-dp
  }
  return 1;
}

/** พักเที่ยงมาตรฐาน — ช่วงลาที่คร่อมพักไม่นับชั่วโมงพัก (fb#345: ลา 09:00–14:00 = 4 ชม. ไม่ใช่ 5) */
export const LUNCH_START = '12:00';
export const LUNCH_END = '13:00';

function hhmmToMin(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

/** นาทีที่ลาจริงในช่วง start–end หักส่วนที่ทับพักเที่ยง */
export function leaveMinutes(start_time: string, end_time: string): number {
  const s = hhmmToMin(start_time);
  const e = hhmmToMin(end_time);
  const lunch = Math.max(0, Math.min(e, hhmmToMin(LUNCH_END)) - Math.max(s, hhmmToMin(LUNCH_START)));
  return Math.max(0, e - s - lunch);
}

/** ช่วงเวลาของลาครึ่งวันตามกะของวันนั้น: เช้า = เข้างาน–12:00 · บ่าย = 13:00–เลิกงาน.
 *  ไม่มีกะ / กะยืดหยุ่น / กะข้ามคืน / กะไม่คร่อมพักเที่ยง → null (ใช้ 0.5 วันแบบเดิม) */
export function halfDayWindow(
  period: 'morning' | 'afternoon',
  shift: { start_time: string; end_time: string; crosses_midnight?: boolean | null; flexible?: boolean | null } | null,
): { start_time: string; end_time: string } | null {
  if (!shift || shift.flexible || shift.crosses_midnight) return null;
  const start = shift.start_time.slice(0, 5);
  const end = shift.end_time.slice(0, 5);
  if (hhmmToMin(start) >= hhmmToMin(LUNCH_START) || hhmmToMin(end) <= hhmmToMin(LUNCH_END)) return null;
  return period === 'morning' ? { start_time: start, end_time: LUNCH_START } : { start_time: LUNCH_END, end_time: end };
}

/** เศษวันที่ leave_records แถวหนึ่งกินจริง — จุดเดียวที่ทุก consumer (reconcile,
 *  โควตา, การแสดงผล) เรียกใช้ เพื่อให้เลขตรงกันเป๊ะ. period/time ว่าง = เต็มวัน.
 *  ส่ง `hoursPerDay` (ชั่วโมงทำงาน/วันของพนักงานคนนั้น) ให้ตรงกันทุก consumer. */
export function leaveRecordDays(
  row: {
    period?: string | null;
    start_time?: string | null;
    end_time?: string | null;
  },
  hoursPerDay: number = HOURS_PER_DAY,
): number {
  return leavePeriodDays(
    (row.period as LeavePeriod) ?? 'full',
    row.start_time ?? undefined,
    row.end_time ?? undefined,
    hoursPerDay,
  );
}

/** วิธีลงบัญชี "วันลา" ของพนักงาน **รายวัน** (ค่าจ้างอิงวันที่มาทำงานจริง = working_days).
 *  ต่างจากรายเดือน/รายชั่วโมงตรงที่ต้องเครดิตเศษวันที่มาทำงานกลับเข้า working_days
 *  ไม่งั้นลาเศษวันแล้วจ่ายขาดทั้งวัน. คืน delta ให้ reconcile เอาไปบวกเข้า summary.
 *  - ลาเต็มวัน (frac>=1): working 0, จองลาเต็ม 1 วัน (เหมือนพฤติกรรมเดิมก่อนมีลาเศษวัน).
 *  - ลาบางส่วน + มีสแกนเข้างาน: working (1-frac), จองลา frac → ลามีสิทธิ์รวมเป็น 1 วันจ่ายเต็ม.
 *  - ลาบางส่วน + ไม่มีสแกน: ไม่เครดิต working (กันจ่ายเกินคนไม่มา), จองเฉพาะ frac, escalate=true
 *    ให้ reconcile ค้าง has_unresolved ให้ HR ตัดสินส่วนที่เหลือ (ห้ามแตะ absent_days ที่ต้องเป็นจำนวนเต็ม).
 *  leaveFrac ควรผ่าน leaveRecordDays มาก่อน (ปัด 3 ตำแหน่ง ตัวหารเดียวกับ submit/quota). */
export function dailyLeaveDayAccrual(
  leaveType: string,
  leaveFrac: number,
  hasMorningScan: boolean,
): { working: number; paidLeave: number; unpaidLeave: number; escalate: boolean } {
  const round3 = (n: number) => Math.round(n * 1000) / 1000;
  const isUnpaid = leaveType === 'unpaid';
  // เต็มวัน (หรือเกิน) → ตรึงที่ 1 วัน ไม่เครดิต working
  if (leaveFrac >= 1) {
    return { working: 0, paidLeave: isUnpaid ? 0 : 1, unpaidLeave: isUnpaid ? 1 : 0, escalate: false };
  }
  const frac = round3(leaveFrac);
  const book = { working: 0, paidLeave: isUnpaid ? 0 : frac, unpaidLeave: isUnpaid ? frac : 0, escalate: false };
  if (!hasMorningScan) return { ...book, escalate: true };
  return { ...book, working: round3(1 - frac) };
}

/** แบ่งวัน "ลากิจ" ที่เกินสิทธิ์ (§34 ให้ค่าจ้างขั้นต่ำ 3 วัน/ปี): ไม่ block แต่เติมวัน
 *  ที่ยังมีสิทธิ์ (จ่าย) จน remaining หมด ที่เหลือเป็นลาไม่รับค่าจ้าง (unpaid). วันที่คร่อมเส้น
 *  (remaining ยังเหลือ > 0 ก่อนถึงวันนั้น) ยังนับเป็นวันมีสิทธิ์เต็มวัน — over-grant เศษได้
 *  เพราะกฎหมายเป็นขั้นต่ำ ห้าม underpay. คืน perDateUnpaid[] (true=วันนั้นเป็น unpaid).
 *  remainingQuota ติดลบได้ (ใช้เกินมาก่อน) → ทุกวันเป็น unpaid. */
export function splitPersonalOverQuota(
  datesCount: number,
  perDateFrac: number,
  remainingQuota: number,
): { perDateUnpaid: boolean[]; unpaidDays: number } {
  let remaining = remainingQuota;
  let unpaidDays = 0;
  const perDateUnpaid: boolean[] = [];
  for (let i = 0; i < datesCount; i++) {
    if (remaining > 1e-9) {
      perDateUnpaid.push(false); // ยังมีสิทธิ์ → จ่ายเต็มวัน
      remaining -= perDateFrac;
    } else {
      perDateUnpaid.push(true); // เกินสิทธิ์ → ลาไม่รับค่าจ้าง
      unpaidDays += perDateFrac;
    }
  }
  return { perDateUnpaid, unpaidDays: Math.round(unpaidDays * 1000) / 1000 };
}

/** แปลง "จำนวนชั่วโมง" ที่พนักงาน/HR กรอก → ช่วงเวลาเริ่ม-สิ้นสุด (ยึดเวลาเริ่มงาน
 *  09:00 เป็นค่าเริ่มต้น) สำหรับเก็บเป็น period='custom'. ใช้ตอน import/ยื่นลาที่ให้
 *  จำนวนชั่วโมงแทนช่วงเวลา. คืน null ถ้าชั่วโมงไม่ถูกต้อง (<=0 หรือเกินวันทำงาน). */
export function hoursToTimeRange(
  hours: number,
  startHHMM = '09:00',
): { start_time: string; end_time: string } | null {
  if (!Number.isFinite(hours) || hours <= 0 || hours > HOURS_PER_DAY) return null;
  const [sh, sm] = startHHMM.split(':').map(Number);
  const startMin = (sh ?? 9) * 60 + (sm ?? 0);
  let endMin = startMin + Math.round(hours * 60);
  // fb#345 — ช่วงที่คร่อมพักเที่ยงต้องยืดออกไปให้ชั่วโมงลาเท่าที่กรอก (leaveMinutes หักพักออก)
  const ls = hhmmToMin(LUNCH_START);
  if (startMin < ls && endMin > ls) endMin += hhmmToMin(LUNCH_END) - ls;
  if (endMin > 24 * 60) return null;
  const fmt = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
  return { start_time: fmt(startMin), end_time: fmt(endMin) };
}

export const EMERGENCY_NOTICE = {
  sick_ms: 60 * 60 * 1000,
  personal_ms: 3 * 24 * 60 * 60 * 1000,
} as const;

export function isEmergencyLeave(leaveType: string, firstDate: string, now = new Date()): boolean {
  const start = new Date(`${firstDate}T00:00:00+07:00`).getTime();
  const advance = start - now.getTime();
  if (leaveType === 'sick') return advance < EMERGENCY_NOTICE.sick_ms;
  if (leaveType === 'personal') return advance < EMERGENCY_NOTICE.personal_ms;
  return false;
}

// เลขบัตรประชาชน — รับช่องว่าง/ขีดกลาง/สัญลักษณ์ที่ช่องกรอกได้ (UX) แต่ preprocess
// ตัดเหลือ "ตัวเลขล้วน" ก่อน validate 13 หลัก (ว่างได้) → ผลหลัง parse สะอาดเสมอ
// ทำให้ทุก endpoint ที่ใช้ field นี้ยืนยันผ่านแม้ผู้ใช้พิมพ์ 1-2345-67890-12-3
// ค่ามาส์ก (มี •) ปล่อยผ่านไม่ตัด — handler ฝั่ง HR จะ drop ทิ้งเอง (กันเขียนทับค่าจริง)
export const nationalIdField = z.preprocess(
  (v) => (typeof v === 'string' && !v.includes('•') ? v.replace(/\D/g, '') : v),
  z
    .string()
    .refine((s) => s === '' || s.includes('•') || /^\d{13}$/.test(s), 'เลขบัตรประชาชนต้อง 13 หลัก')
    .optional(),
);

// === Self-profile (employee fills the 10 fields tracked for completion %) ===
export const updateProfileSchema = z.object({
  // Identity
  national_id: nationalIdField,
  birth_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'รูปแบบวันที่: YYYY-MM-DD')
    .optional()
    .or(z.literal('')),
  phone: z.string().max(20).optional().or(z.literal('')),
  sso_number: z.string().max(20).optional().or(z.literal('')),
  bank: z.string().max(50).optional().or(z.literal('')),
  bank_account_masked: z.string().max(30).optional().or(z.literal('')),
  // fb#254 — เลขบัญชีเต็ม (เดิมหน้าโปรไฟล์เก็บแค่ 4 ตัวท้าย → ไฟล์โอนเงินเดือนไม่มีเลขบัญชี)
  bank_account_full: z
    .string()
    .transform((v) => v.replace(/[-\s]/g, ''))
    .refine((v) => v === '' || /^\d{10,15}$/.test(v), 'เลขบัญชีต้องเป็นตัวเลข 10-15 หลัก')
    .optional(),
  // Documents — R2 paths from /upload (purpose=identity|evidence)
  id_copy_path: z.string().max(500).optional().or(z.literal('')),
  bankbook_path: z.string().max(500).optional().or(z.literal('')),
  // Address
  address_current: z.string().max(500).optional().or(z.literal('')),
  address_registered: z.string().max(500).optional().or(z.literal('')),
  // Emergency contact
  emergency_name: z.string().max(100).optional().or(z.literal('')),
  emergency_relationship: z.string().max(50).optional().or(z.literal('')),
  emergency_phone: z.string().max(20).optional().or(z.literal('')),
});
export type UpdateProfileRequest = z.infer<typeof updateProfileSchema>;

// === Education + Work-history (N-row sub-resources, optional, NOT in completion %) ===
export const upsertEducationSchema = z.object({
  id: z.string().uuid().optional(),     // omitted = create
  degree: z.string().min(1).max(50),
  institution: z.string().min(1).max(200),
  major: z.string().max(200).optional().or(z.literal('')),
  year_start: z.number().int().min(1900).max(2100).optional().nullable(),
  year_end: z.number().int().min(1900).max(2100).optional().nullable(),
  gpa: z.number().min(0).max(4.5).optional().nullable(),
  note: z.string().max(500).optional().or(z.literal('')),
});
export type UpsertEducationRequest = z.infer<typeof upsertEducationSchema>;

export const upsertWorkHistorySchema = z.object({
  id: z.string().uuid().optional(),
  company: z.string().min(1).max(200),
  position: z.string().min(1).max(200),
  start_date: dateString.optional().or(z.literal('')).nullable(),
  end_date: dateString.optional().or(z.literal('')).nullable(),
  reason_for_leaving: z.string().max(500).optional().or(z.literal('')),
  note: z.string().max(500).optional().or(z.literal('')),
});
export type UpsertWorkHistoryRequest = z.infer<typeof upsertWorkHistorySchema>;

export const createEmployeeDocumentSchema = z.object({
  label: z.string().min(1).max(100),
  r2_path: z.string().min(1).max(500),
  mime_type: z.string().max(100).optional(),
  size_bytes: z.number().int().nonnegative().optional(),
  note: z.string().max(500).optional().or(z.literal('')),
});
export type CreateEmployeeDocumentRequest = z.infer<typeof createEmployeeDocumentSchema>;

// === หนังสือเตือน / ใบเตือน (disciplinary warnings) ===
export const createEmployeeWarningSchema = z.object({
  warning_date: dateString,
  level: z.enum(['verbal', 'written', 'final']).default('written'),
  subject: z.string().min(1).max(200),
  detail: z.string().max(2000).optional().or(z.literal('')),
  issued_by: z.string().max(100).optional().or(z.literal('')),
  acknowledged: z.boolean().optional().default(false),
  // ไฟล์แนบ (รูป/PDF ใบเตือนตัวจริง) — optional
  r2_path: z.string().max(500).optional().or(z.literal('')),
  mime_type: z.string().max(100).optional(),
  size_bytes: z.number().int().nonnegative().optional(),
  note: z.string().max(500).optional().or(z.literal('')),
});
export type CreateEmployeeWarningRequest = z.infer<typeof createEmployeeWarningSchema>;

export const respondInfoRequestSchema = z.object({
  leave_id: z.string(),
  response_text: z.string().min(1).max(1000),
  evidence_path: z.string().optional(),
});

export const submitConditionalEvidenceSchema = z.object({
  leave_id: z.string(),
  evidence_path: z.string().min(1),
});

// === OT ===
export const submitOTSchema = z
  .object({
    date: dateString,
    start_time: timeString,
    end_time: timeString,
    // ผู้ใช้ไม่ต้องเลือกประเภท — server derive จากวันที่เสมอ (กันคิดผิด). optional ไว้
    // เผื่อ client เก่ายังส่งมา แต่ค่าจะถูกเพิกเฉย.
    ot_type: z.enum(OT_TYPES as readonly [string, ...string[]]).optional(),
    reason: z.string().min(1).max(500),
  })
  // เวลาสิ้นสุด ≤ เวลาเริ่ม เคยถูกตีความเป็น "ข้ามคืน +24 ชม." → ผู้ใช้กรอกสลับ
  // ได้ OT โป่งโดยไม่รู้ตัว. บล็อกที่ schema เพื่อครอบทุกช่องทาง (LIFF/client เก่า).
  .superRefine((v, ctx) => {
    if (v.end_time <= v.start_time) {
      ctx.addIssue({
        code: 'custom',
        message: 'เวลาสิ้นสุดต้องอยู่หลังเวลาเริ่ม',
        path: ['end_time'],
      });
    }
  });
export type SubmitOTRequest = z.infer<typeof submitOTSchema>;

// === ยกเลิกคำขอของตัวเอง (ลา/OT ที่ยังรออนุมัติ) ===
export const cancelRequestSchema = z.object({
  kind: z.enum(['leave', 'ot']),
  record_id: z.string().min(1), // leave_records.id / ot_requests.id (uuid)
});
export type CancelRequest = z.infer<typeof cancelRequestSchema>;

// === Owner/HR ขอยกเลิกใบที่ "อนุมัติแล้ว แต่ยังมาไม่ถึง" (ต้องให้พนักงานยินยอม) ===
export const requestCancelApprovedSchema = z.object({
  kind: z.enum(['leave', 'ot']),
  record_id: z.string().min(1),
  reason: z.string().min(1, 'ต้องระบุเหตุผล').max(500),
});
export type RequestCancelApproved = z.infer<typeof requestCancelApprovedSchema>;

// === พนักงานตอบคำขอยกเลิกของ owner/HR (ยินยอม / ไม่ยินยอม) ===
export const consentCancelSchema = z.object({
  kind: z.enum(['leave', 'ot']),
  record_id: z.string().min(1),
  consent: z.boolean(), // true = ยินยอมให้ยกเลิก, false = ไม่ยินยอม (คงใบไว้)
});
export type ConsentCancel = z.infer<typeof consentCancelSchema>;

// === Check-in ===
export const CHECKIN_KINDS = ['morning_in', 'lunch_out', 'lunch_in', 'evening_out'] as const;
export type CheckinKind = (typeof CHECKIN_KINDS)[number];

export const submitCheckinSchema = z.object({
  selfie_path: z.string().min(1), // R2 path from POST /upload
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  kind: z.enum(CHECKIN_KINDS),
});
export type SubmitCheckinRequest = z.infer<typeof submitCheckinSchema>;

// === Upload presign ===
export const FILE_PURPOSES = ['selfie', 'evidence', 'avatar', 'identity', 'temp'] as const;
export type FilePurposeShared = (typeof FILE_PURPOSES)[number];

export const presignUploadSchema = z.object({
  ext: z.enum(['jpg', 'jpeg', 'png', 'pdf']),
  purpose: z.enum(['selfie', 'evidence', 'avatar', 'identity']),
});
export type PresignUploadRequest = z.infer<typeof presignUploadSchema>;

// === Approval action ===
export const approvalActionSchema = z.object({
  kind: z.enum(['leave', 'ot']),
  id: z.string(),                        // leave_id or ot_id
  action: z.enum(['approve', 'reject', 'request_info', 'conditional_approve']),
  reason: z.string().max(500).optional(),
  info_message: z.string().max(500).optional(),
  evidence_due_at: z.string().optional(),  // ISO datetime for conditional
  // fb#606: ใบลาที่ค้างข้ามงวดที่ปิดจ่ายแล้ว — อนุมัติเพื่อให้สถิติการลาตรง โดยไม่ย้อนไปคิดเงินงวดนั้น
  locked_period_record_only: z.boolean().optional(),
});
export type ApprovalActionRequest = z.infer<typeof approvalActionSchema>;

// === HR CRUD ===
export const upsertEmployeeSchema = z.object({
  emp_code: empCodeString,
  // ไม่มีช่องบังคับในหน้าข้อมูลพนักงาน — ชื่อ/นามสกุลกรอกทีหลังได้
  // (handler จะ default เป็น '' กัน NOT NULL ของคอลัมน์ใน DB)
  first_name: z.string().optional().or(z.literal('')),
  last_name: z.string().optional().or(z.literal('')),
  nickname: z.string().optional().or(z.literal('')),
  // email/department/position/start_date are optional — a minimal add fills
  // only name + national_id; the rest comes later (employee via LINE / HR).
  email: z.string().email().optional().or(z.literal('')),
  phone: z.string().optional().or(z.literal('')),
  department: z.string().optional().or(z.literal('')),
  position: z.string().optional().or(z.literal('')),
  supervisor_email: z.string().email().optional().or(z.literal('')),
  // หัวหน้างาน = เลือกชื่อพนักงาน (emp_code). '' = ไม่มีหัวหน้า
  supervisor_emp_code: z.string().optional().or(z.literal('')),
  start_date: dateString.optional().or(z.literal('')),
  end_date: dateString.optional().or(z.literal('')),
  // P07 (/hr-personas) — suspended = พักงาน ไม่คิดเงินเดือนชั่วคราว แต่ยังเป็นพนักงาน
  //   ไม่มี end_date (ต่างจาก resigned/terminated ที่ handler บังคับกรอก) · กลับมาทำงานแค่ตั้งกลับเป็น active
  status: z.enum(['active', 'probation', 'suspended', 'resigned', 'terminated']),
  // แหล่งตัดสินวันหยุดตอนคิดเงิน (วันหยุดหมุนเวียน) — ดู SHIFT_ROSTER_BLUEPRINT.md
  //   company_weekend = ใช้วันหยุดบริษัท (weekend_days) · roster = ยึดตารางเวรรายคน
  schedule_mode: z.enum(['company_weekend', 'roster', 'on_call']).optional(),
  // ไม่ต้องสแกนเข้างาน/ไม่นับเวลา → จ่ายเต็ม (เจาะรายคน เช่น ผู้บริหาร/เหมาเดือน)
  attendance_exempt: z.boolean().optional(),
  // ไม่ทำเงินเดือนในระบบ (เจ้าของบัญชี/ผู้ดูแล) — true = ไม่เข้าสรุปเวลา/ร่างเงินเดือน/ปิดงวด
  exclude_from_payroll: z.boolean().optional(),
  // สแกนออกรายคน: null=ตามบริษัท/กะ · false=สแกนเข้าอย่างเดียว · true=ต้องสแกนออก
  require_clock_out: z.boolean().nullable().optional(),
  // ประกันสังคม/ภาษีรายคน: null|standard = สปส.+ภ.ง.ด.1 · wht_pnd3 = ไม่หัก สปส. หัก ณ ที่จ่ายอัตราคงที่ (ภ.ง.ด.3)
  statutory_mode: z.enum(['standard', 'wht_pnd3', 'no_deduction']).nullable().optional(),
  // ยืนยันวันเริ่มงานที่อยู่หลังวันที่มีสแกนเข้างานจริง (ระบบถามกลับก่อนบันทึก)
  confirm_start_after_scans: z.boolean().optional(),
  // แพทเทิร์นหมุนเวรอัตโนมัติ — ผูก pattern + วันเริ่มรอบ (ส่ง '' = ปลดออก)
  rotation_pattern_id: z.string().uuid().nullable().optional().or(z.literal('')),
  rotation_anchor_date: dateString.nullable().optional().or(z.literal('')),
  // Profile fields used by compute_profile_completion_pct trigger
  national_id: nationalIdField,
  birth_date: dateString.optional().or(z.literal('')),
  sso_number: z.string().optional().or(z.literal('')),
  bank: z.string().optional().or(z.literal('')),
  // สาขาธนาคาร — ใช้ประกอบไฟล์โอนเงินเดือนบาง format (เช่น SCB ใช้รหัสสาขาตัวเลข)
  bank_branch: z.string().max(100).optional().or(z.literal('')),
  bank_account_masked: z.string().optional().or(z.literal('')),
  // Full account number for the bank-transfer file. Stored encrypted
  // (bank_account_encrypted); never persisted as plaintext. Handler derives
  // bank_account_masked (last 4) from it. Send '' to leave unchanged.
  bank_account_full: z.string().max(30).regex(/^[0-9-]*$/, 'เลขบัญชีตัวเลขเท่านั้น').optional().or(z.literal('')),
  // Extended profile (added 2026-05-21)
  id_copy_path: z.string().max(500).optional().or(z.literal('')),
  bankbook_path: z.string().max(500).optional().or(z.literal('')),
  address_current: z.string().max(500).optional().or(z.literal('')),
  address_registered: z.string().max(500).optional().or(z.literal('')),
  emergency_name: z.string().max(100).optional().or(z.literal('')),
  emergency_relationship: z.string().max(50).optional().or(z.literal('')),
  emergency_phone: z.string().max(20).optional().or(z.literal('')),
});

export const upsertAllowanceSchema = z.object({
  emp_code: empCodeString,
  category: z.string().min(1),
  amount: z.number().nonnegative(),
  effective_from: dateString,
  effective_to: dateString.optional(),
  note: z.string().optional(),
});

export const upsertHolidaySchema = z.object({
  date: dateString,
  name: z.string().min(1),
  type: z.enum(['public', 'company', 'substitute']),
  applies_to: z.string().default('all'),
});

// === Dept holiday override (เฟส 1 — วันหยุดนักขัตฤกษ์รายแผนก) ===
// ตั้งครั้งเดียว = สลับวันหยุด (kind='work' วันนักขัตฤกษ์ต้นทาง) + วันชดเชย
// (kind='off') คู่กันเสมอ — ยกเว้นติ๊ก no_comp ยืนยันชัดเจนว่าไม่ให้วันชดเชย.
// confirm=false (default) → handler คืน impact preview ก่อนบันทึกจริง.
export const upsertDeptHolidayOverrideSchema = z
  .object({
    dept_name: z.string().min(1).max(80),
    work_date: dateString, // วันหยุดนักขัตฤกษ์ต้นทางที่แผนกนี้ทำงาน
    off_date: dateString.optional(), // วันชดเชยของแผนก (บังคับ เว้นแต่ no_comp)
    off_name: z.string().max(120).optional(),
    no_comp: z.boolean().optional().default(false),
    allow_backdated: z.boolean().optional().default(false),
    confirm: z.boolean().optional().default(false),
  })
  .refine((v) => v.no_comp || !!v.off_date, {
    message: 'ต้องระบุวันชดเชย หรือติ๊กยืนยันว่าไม่ให้วันชดเชย',
    path: ['off_date'],
  });

export const upsertLeaveQuotaSchema = z.object({
  emp_code: empCodeString,
  year: z.number().int().min(2020).max(2100),
  sick_quota: z.number().nonnegative(),
  personal_quota: z.number().nonnegative(),
  vacation_quota: z.number().nonnegative(),
  carried_days: z.number().nonnegative().optional(),
});

// === Leave packages (2026-05-23) ===
export const upsertLeavePackageSchema = z.object({
  id: z.string().uuid().optional(),
  name: z.string().min(1).max(80),
  description: z.string().max(280).optional().nullable(),
  sick_quota: z.number().nonnegative(),
  personal_quota: z.number().nonnegative(),
  vacation_quota: z.number().nonnegative(),
  prorate_on_join: z.boolean().default(true),
  carry_over_max: z.number().nonnegative().default(0),
  /** กฎ §30 ม.แรงงาน — false (default) = ปีแรกไม่ได้สิทธิ์พักร้อน, true = ได้ตั้งแต่วันแรก */
  vacation_first_year_eligible: z.boolean().default(false),
});

export const assignLeavePackageSchema = z.object({
  package_id: z.string().uuid(),
  year: z.number().int().min(2020).max(2100),
  emp_codes: z.array(empCodeString).min(1).max(500),
  /** If true (default), prorate quotas based on employees.start_date for the
   *  given year. False = full quota regardless of join date. */
  apply_prorate: z.boolean().default(true),
});

export const rolloverLeaveSchema = z.object({
  /** Year that's ending. Carry-over flows from {year} → {year+1}. */
  from_year: z.number().int().min(2020).max(2100),
});

// === Owner / Payroll ===
export const runReconcileSchema = z.object({
  period: periodString,
  /** 'final' = period_end already passed; 'preview' = mid-period peek
   *  with projection warning for un-elapsed days. */
  mode: z.enum(['final', 'preview']).optional().default('final'),
});

export const runPayrollSchema = z.object({
  period: periodString,
  force: z.boolean().default(false),
  /** Optional trigger reason recorded on the payroll_revisions row. */
  trigger_reason: z.string().max(50).optional(),
  /**
   * โหมดเงินเดือนล้วน (per-run override). undefined = ใช้ค่าบริษัท
   * (tenant_settings.salary_only). true = งวดนี้ข้ามการลงเวลา/ลา/OT จ่ายเต็ม
   * (ฐาน+เพิ่ม+หัก+ภาษี/ปกส.). false = บังคับคิดตามการลงเวลาแม้บริษัทตั้ง salary_only.
   */
  salary_only: z.boolean().optional(),
});

export const payrollPeriodActionSchema = z.object({
  period: periodString,
});

/** ซ้อมทำเงินเดือน — คิดครบทุกกติกาของงวดที่เลือกโดยไม่บันทึกอะไร (ไม่แตะสรุปเวลา/ร่าง/ประวัติ) */
export const rehearsePayrollSchema = z.object({
  period: periodString,
  /** เหมือน runPayrollSchema.salary_only — undefined = ตามค่าบริษัท */
  salary_only: z.boolean().optional(),
});

export const payrollRejectSchema = z.object({
  period: periodString,
  reason: z.string().min(1).max(500),
});

// B6 — Owner-only unlock out of locked/pdf_ready/sending/partial_sent/sent.
// reason is mandatory (audited as a WARN entry, not just logged/ignored).
export const payrollUnlockSchema = z.object({
  period: periodString,
  reason: z.string().min(1).max(500),
});

// === เบี้ยขยันขั้นบันได (diligence ladder) ===
export const DILIGENCE_LEAVE_TYPES = ['sick', 'personal', 'vacation', 'unpaid', 'maternity'] as const;
export const diligenceLadderSchema = z.object({
  active: z.boolean().default(true),
  name: z.string().max(100).optional(),
  // จำนวนเงินแต่ละขั้น (index 0 = ขั้น 1) — ต้องเรียงและไม่ติดลบ
  steps: z.array(z.number().nonnegative()).max(20).default([]),
  start_tier: z.number().int().min(1).default(1),
  break_on_late: z.boolean().default(true),
  break_on_absent: z.boolean().default(true),
  break_leave_types: z.array(z.enum(DILIGENCE_LEAVE_TYPES)).default([]),
  // เดือนมีปัญหา → ได้ 0 + เริ่มนับใหม่ขั้น 1 (false = ลดทีละขั้น)
  reset_on_break: z.boolean().default(false),
  // ต้องเข้าก่อนกะ ≥ N นาที ทุกวัน ถึงนับเดือนสะอาด (0 = ไม่บังคับ)
  require_early_minutes: z.number().int().min(0).max(240).default(0),
  is_taxable: z.boolean().default(true),
  is_ssoable: z.boolean().default(true),
  is_pfable: z.boolean().default(true),
});
export type DiligenceLadderInput = z.infer<typeof diligenceLadderSchema>;

// === Phase 1B — Allowance master CRUD ===
export const ALLOWANCE_PRESETS = [
  'position', 'cola', 'travel_fixed', 'travel_actual',
  'meal', 'phone_actual', 'commission_base', 'custom',
] as const;
export const ALLOWANCE_FREQUENCIES = ['fixed_monthly', 'variable'] as const;

export const upsertAllowanceMasterSchema = z.object({
  id: z.string().uuid().optional(),
  code: z.string().min(1).max(40),
  name_th: z.string().min(1).max(100),
  preset_type: z.enum(ALLOWANCE_PRESETS),
  is_taxable: z.boolean().default(true),
  is_ssoable: z.boolean().default(true),
  is_pfable: z.boolean().default(true),
  frequency: z.enum(ALLOWANCE_FREQUENCIES).default('fixed_monthly'),
  default_amount: z.number().nonnegative().nullable().optional(),
  default_rate: z.number().nonnegative().nullable().optional(),
  rate_unit: z.string().max(20).nullable().optional(),
  condition_type: z.enum(['perfect_attendance']).nullable().optional(),
  active: z.boolean().default(true),
});

export const upsertAllowanceEligibilitySchema = z.object({
  id: z.string().uuid().optional(),
  emp_code: z.string().min(1).max(20),
  allowance_id: z.string().uuid(),
  fixed_amount: z.number().nonnegative().nullable().optional(),
  rate: z.number().nonnegative().nullable().optional(),
  start_date: dateString,
  end_date: dateString.optional().or(z.literal('')).nullable(),
  active: z.boolean().default(true),
});

// === Recurring deduction master + eligibility ===
export const DEDUCTION_KINDS = ['statutory', 'fixed'] as const;

export const upsertDeductionMasterSchema = z.object({
  id: z.string().uuid().optional(),
  code: z.string().min(1).max(40),
  name_th: z.string().min(1).max(100),
  kind: z.enum(DEDUCTION_KINDS).default('fixed'),
  default_amount: z.number().nonnegative().nullable().optional(),
  note: z.string().max(200).nullable().optional(),
  active: z.boolean().default(true),
});

export const upsertDeductionEligibilitySchema = z.object({
  id: z.string().uuid().optional(),
  emp_code: z.string().min(1).max(20),
  deduction_id: z.string().uuid(),
  amount: z.number().nonnegative().nullable().optional(),
  start_date: dateString,
  end_date: dateString.optional().or(z.literal('')).nullable(),
  active: z.boolean().default(true),
});

// === เรื่อง 1 — emergency leave from a missed scan ===
export const requestEmergencyLeaveSchema = z.object({
  date: dateString,
  reason: z.string().max(500).optional().or(z.literal('')),
});

export const decideEmergencyLeaveSchema = z.object({
  leave_id: z.string().min(1),
  /** paid = ลาได้เงิน; unpaid = ลาไม่ได้เงิน; absent = ขาดงาน */
  decision: z.enum(['paid', 'unpaid', 'absent']),
  reason: z.string().min(1).max(500),
  /** HR's call: also deduct from the leave quota? */
  deduct_quota: z.boolean(),
});

// === เรื่อง 3 — self-confirm clock-out ===
export const confirmClockOutSchema = z.object({
  date: dateString,
  /** 'shift_end' = ใช้เวลาเลิกตามกะ (ปลอดภัย ไม่ overpay); 'actual' = ระบุเวลาจริง. */
  mode: z.enum(['shift_end', 'actual']),
  /** required when mode='actual' */
  actual_time: timeString.optional(),
  reason: z.string().max(300).optional().or(z.literal('')),
});

// === Tickets (ร้องเรียน / แจ้งเหตุ / ร้องทุกข์ + คำขอแก้ย้อนหลัง) ===
export const TICKET_CATEGORIES = [
  'complaint', 'report', 'grievance',
  'leave_backdate', 'leave_type_change', 'leave_edit_locked', 'schedule_change',
] as const;
export type TicketCategory = (typeof TICKET_CATEGORIES)[number];

export const createTicketSchema = z.object({
  category: z.enum(TICKET_CATEGORIES),
  subject: z.string().min(1).max(150),
  detail: z.string().min(1).max(2000),
  ref_note: z.string().max(300).optional().or(z.literal('')),
});

export const triageTicketSchema = z.object({
  id: z.string().uuid(),
  action: z.enum(['review', 'escalate', 'resolve']),
  hr_note: z.string().max(1000).optional().or(z.literal('')),
});

export const decideTicketSchema = z.object({
  id: z.string().uuid(),
  decision: z.enum(['approved', 'rejected']),
  decision_note: z.string().min(1).max(1000),
});

// === Phase 1B — Data entry endpoints ===
export const upsertVariableAllowanceEntrySchema = z.object({
  period: periodString,
  emp_code: z.string().min(1).max(20),
  allowance_id: z.string().uuid(),
  qty: z.number().nonnegative(),
});

/**
 * Adjustment presets — decide how a one-off "เงินเพิ่ม" hits the
 * tax / SSO / PF bases. Tax ≠ SSO: a bonus is taxable income but is NOT
 * "ค่าจ้าง" for SSO (ม.5 พรบ.ปกส.), so it must not enter the SSO base.
 * Presets apply to direction='add' only (income); deductions ignore them.
 */
export const ADJUSTMENT_PRESETS = {
  // โบนัส / เงินรางวัล / เงินเพิ่มพิเศษ — เสียภาษี แต่ไม่เข้า ปกส./PF
  bonus: { is_taxable: true, is_ssoable: false, is_pfable: false },
  // ปรับค่าจ้างตกหล่น (เช่น OT/ค่าจ้างที่ลืมจ่าย) — เข้าทุกฐาน
  wage_correction: { is_taxable: true, is_ssoable: true, is_pfable: true },
  // เบิกคืนตามจริง / ไม่ใช่เงินได้ — ไม่เข้าฐานใดเลย
  reimbursement: { is_taxable: false, is_ssoable: false, is_pfable: false },
} as const;
export type AdjustmentPreset = keyof typeof ADJUSTMENT_PRESETS;

export const upsertAdjustmentEntrySchema = z.object({
  period: periodString,
  emp_code: z.string().min(1).max(20),
  direction: z.enum(['add', 'deduct']),
  label: z.string().min(1).max(100),
  amount: z.number().positive(),
  /** Tax/SSO/PF treatment for an 'add'. Default 'bonus' (taxable, not SSO).
   *  Ignored for 'deduct'. */
  preset: z.enum(['bonus', 'wage_correction', 'reimbursement']).optional(),
  notes: z.string().max(500).optional().or(z.literal('')),
});

// === Phase 3 — Shift master ===
export const NIGHT_PREMIUM_TYPES = ['none', 'percent', 'flat'] as const;

export const upsertShiftMasterSchema = z.object({
  id: z.string().uuid().optional(),
  code: z.string().min(1).max(40),
  name: z.string().min(1).max(100),
  start_time: timeString,
  end_time: timeString,
  crosses_midnight: z.boolean().default(false),
  break_minutes: z.number().int().nonnegative().default(60),
  paid_hours: z.number().positive().max(24).default(8),
  night_premium_type: z.enum(NIGHT_PREMIUM_TYPES).default('none'),
  night_premium_value: z.number().nonnegative().default(0),
  night_window_start: timeString.default('22:00'),
  night_window_end: timeString.default('06:00'),
  default_weekdays: z.array(z.number().int().min(1).max(7)).default([1, 2, 3, 4, 5, 6]),
  active: z.boolean().default(true),
  // กะยืดหยุ่น: ไม่คิดสาย · สแกนเข้าอย่างเดียว (ไม่บังคับออก) · ต้องสแกนเข้าภายใน flex_check_in_by
  flexible: z.boolean().default(false),
  require_clock_out: z.boolean().default(true),
  flex_check_in_by: timeString.nullable().optional(),
});

export const upsertShiftAssignmentSchema = z.object({
  id: z.string().uuid().optional(),
  emp_code: z.string().min(1).max(20),
  shift_id: z.string().uuid(),
  weekdays: z.array(z.number().int().min(1).max(7)).min(1),
  start_date: dateString,
  end_date: dateString.optional().or(z.literal('')).nullable(),
  active: z.boolean().default(true),
});

export const upsertShiftRosterSchema = z.object({
  emp_code: z.string().min(1).max(20),
  date: dateString,
  shift_id: z.string().uuid().nullable(),  // null = explicit day-off
  note: z.string().max(200).optional().or(z.literal('')),
});

// === วันหยุดหมุนเวียน / นำเข้าตารางเวร (P2/P3) ===

// บันทึกหลายช่องรวดเดียว (drag-paint ในปฏิทิน). shift_id=null = วันหยุด.
export const upsertShiftRosterBulkSchema = z.object({
  entries: z.array(z.object({
    emp_code: z.string().min(1).max(20),
    date: dateString,
    shift_id: z.string().uuid().nullable(),
    note: z.string().max(200).optional().or(z.literal('')),
  })).min(1).max(3000),
});

// ตารางแปลรหัส (Code Mapping) ระดับ tenant — เก็บใน settings เป็น JSON.
// key = ข้อความดิบ (normalize แล้ว) · value = shift code | '__OFF__' | '__IGNORE__'
export const shiftCodeAliasSchema = z.object({
  aliases: z.record(z.string(), z.string()),
});

// นำเข้าตารางเวรจาก Excel/paste — browser parse เป็น grid คน×วัน แล้วส่ง raw
// (preview + commit ใช้ schema เดียวกัน; backend map+validate ฝั่งเดียวเป็น authority)
export const shiftRosterImportSchema = z.object({
  period: periodString,                  // YYYY-MM (เดือนที่จะลง)
  rows: z.array(z.object({
    key: z.string().min(1).max(120),     // emp_code หรือ ชื่อ (backend จับคู่เอง)
    cells: z.record(z.string(), z.string()),  // "1".."31" (วันที่ของเดือน) → ข้อความดิบ
  })).min(1).max(500),
});

// ดึงตารางเดือนก่อนมาเป็นฐานเดือนใหม่
export const rosterCopyPrevMonthSchema = z.object({
  period: periodString,                  // เดือนปลายทาง YYYY-MM
  overwrite: z.boolean().default(false), // ทับของเดิมในเดือนปลายทางไหม
});

// แพทเทิร์นหมุนเวร: sequence = ลำดับกะ 1 รอบ (uuid = กะ, null = วันหยุด)
export const rotationPatternSchema = z.object({
  id: z.string().uuid().optional(),
  code: z.string().min(1).max(40),
  name: z.string().min(1).max(100),
  sequence: z.array(z.string().uuid().nullable()).min(1).max(60),
  active: z.boolean().default(true),
});

// generate ตารางหมุนของเดือน → เขียน shift_roster (ข้ามช่อง manual)
export const generateRotationSchema = z.object({
  period: periodString,
});

// คู่สลับตอบรับ/ปฏิเสธคำขอสลับเวร
export const swapRespondSchema = z.object({
  accept: z.boolean(),
});

export const createSwapRequestSchema = z.object({
  date: dateString,
  requester_emp_code: z.string().min(1).max(20),
  target_emp_code: z.string().min(1).max(20),
  reason: z.string().max(300).optional().or(z.literal('')),
});

export const decideSwapRequestSchema = z.object({
  decision: z.enum(['approved', 'rejected']),
  decision_note: z.string().max(300).optional().or(z.literal('')),
});

// === ขอย้ายวันหยุด (shift change request) ===
export const createShiftChangeRequestSchema = z.object({
  original_date: dateString,
  target_date: dateString,
  // กะที่จะเข้าทำงานในวันที่กลับมาทำงาน (วันหยุดเดิม). ว่าง = ใช้กะที่ resolve ได้.
  original_shift_id: z.string().uuid().optional().or(z.literal('')),
  reason: z.string().max(300).optional().or(z.literal('')),
});
export type CreateShiftChangeRequest = z.infer<typeof createShiftChangeRequestSchema>;

export const decideShiftChangeRequestSchema = z.object({
  decision: z.enum(['approved', 'rejected']),
  rejection_reason: z.string().max(300).optional().or(z.literal('')),
});
export type DecideShiftChangeRequest = z.infer<typeof decideShiftChangeRequestSchema>;

// fb#609 — ขอเปลี่ยนเวลาเข้า-ออกงานรายวัน (กะเดิม ชั่วโมงเท่าเดิม) · เวลาเลิกให้เซิร์ฟเวอร์คำนวณเอง
export const createShiftTimeRequestSchema = z.object({
  work_date: dateString,
  new_start_time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'รูปแบบเวลา HH:mm'),
  reason: z.string().trim().min(5, 'ระบุเหตุผลอย่างน้อย 5 ตัวอักษร').max(300),
});
export type CreateShiftTimeRequest = z.infer<typeof createShiftTimeRequestSchema>;

export const decideShiftTimeRequestSchema = z.object({
  decision: z.enum(['approved', 'rejected']),
  rejection_reason: z.string().max(300).optional().or(z.literal('')),
});

export const cancelShiftTimeRequestSchema = z.object({
  reason: z.string().trim().max(300).optional().or(z.literal('')),
});

// === Phase 2B — Termination ===
export const TERMINATION_REASONS = ['resigned', 'terminated', 'terminated_for_cause', 'retired', 'contract_end'] as const;

export const terminationPreviewSchema = z.object({
  emp_code: z.string().min(1).max(20),
  effective_date: dateString,
  reason_code: z.enum(TERMINATION_REASONS),
  /** Days of notice the employer failed to give (0 if proper notice). */
  notice_shortfall_days: z.number().int().nonnegative().optional().default(0),
  /** Override accrued leave days if HR computes differently. */
  accrued_leave_days_override: z.number().nonnegative().optional(),
});

export const terminationCommitSchema = terminationPreviewSchema.extend({
  reason_note: z.string().max(500).optional().or(z.literal('')),
  /** Payroll period (YYYY-MM) to feed the payout into as adjustment_add. */
  feeds_payroll_period: periodString,
});

// === Phase 1C — Commission ===
export const COMMISSION_BASES = ['gross_sales', 'units_sold', 'gross_profit'] as const;

export const upsertCommissionPackageSchema = z.object({
  id: z.string().uuid().optional(),
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional().or(z.literal('')),
  basis: z.enum(COMMISSION_BASES),
  min_threshold: z.number().nonnegative().default(0),
  cap: z.number().nullable().optional(),
  floor: z.number().nullable().optional(),
  active: z.boolean().default(true),
  tiers: z
    .array(
      z.object({
        tier_order: z.number().int().min(1),
        min_amount: z.number().nonnegative(),
        max_amount: z.number().nullable().optional(),
        rate_percent: z.number().nullable().optional(),
        rate_fixed: z.number().nullable().optional(),
        notes: z.string().max(200).optional().or(z.literal('')),
      }),
    )
    .min(1),
});

export const upsertCommissionEligibilitySchema = z.object({
  id: z.string().uuid().optional(),
  emp_code: z.string().min(1).max(20),
  package_id: z.string().uuid(),
  start_date: dateString,
  end_date: dateString.optional().or(z.literal('')).nullable(),
  override_cap: z.number().nullable().optional(),
  override_floor: z.number().nullable().optional(),
  active: z.boolean().default(true),
});

export const createCommissionRunSchema = z.object({
  period_start: dateString,
  period_end: dateString,
});

export const upsertCommissionPerformanceSchema = z.object({
  emp_code: z.string().min(1).max(20),
  gross_sales: z.number().nonnegative().optional().default(0),
  units_sold: z.number().nonnegative().optional().default(0),
  gross_profit: z.number().nonnegative().optional().default(0),
  notes: z.string().max(500).optional().or(z.literal('')),
});

export const lockCommissionRunSchema = z.object({
  /** YYYY-MM — the payroll period that should receive these commission
   *  amounts as data_entries (entry_type='adjustment_add'). */
  feeds_payroll_period: periodString,
});

export const dryRunPayrollSchema = z.object({
  period: periodString,
  /** Per-emp tweaks to apply on top of currently saved data entries
   *  — only affects this in-memory recalc, no rows touched. */
  tweaks: z
    .array(
      z.object({
        emp_code: z.string(),
        additions: z.number().optional(),
        deductions: z.number().optional(),
      }),
    )
    .optional()
    .default([]),
});

export const onboardEmployeeSchema = z.object({
  // Optional: blank/omitted → server auto-generates the next running code
  // (EMP0001, EMP0002, …) scoped to the tenant.
  emp_code: z.string().trim().max(20).optional(),
  first_name: z.string().min(1),
  last_name: z.string().min(1),
  email: z.string().email(),
  department: z.string().min(1),
  position: z.string().min(1),
  start_date: dateString,
  base_salary: z.number().positive(),     // goes to Secret layer
  invite_role: z.enum(['EMPLOYEE', 'HR', 'SUPERVISOR', 'MANAGER']).default('EMPLOYEE'),
});

// Re-issue a pairing code for an EXISTING employee (link LINE later / re-link
// after revoke). Role defaults to EMPLOYEE when omitted.
export const reissuePairingSchema = z.object({
  role: z.enum(['EMPLOYEE', 'HR', 'SUPERVISOR', 'MANAGER']).default('EMPLOYEE'),
});
export type ReissuePairing = z.infer<typeof reissuePairingSchema>;

// เปลี่ยนรหัสพนักงาน (emp_code) — propagate ทั้งระบบ. owner + HR เต็มสิทธิ์เท่านั้น
// (gate ด้วย capability 'rename_emp_code'). รหัสใหม่ = 1–20 ตัว ห้ามว่าง.
export const renameEmpCodeSchema = z.object({
  new_emp_code: empCodeString,
});
export type RenameEmpCode = z.infer<typeof renameEmpCodeSchema>;

// === แจ้งปัญหา / ขอฟีเจอร์ (HR/Owner) ===
export const feedbackMediaSchema = z.object({
  path: z.string().min(1),
  type: z.string().max(100).optional().or(z.literal('')),
  name: z.string().max(200).optional().or(z.literal('')),
});
export const createFeedbackSchema = z.object({
  kind: z.enum(['bug', 'feature']),
  title: z.string().trim().min(1).max(200),
  detail: z.string().max(5000).optional().or(z.literal('')),
  page_url: z.string().max(500).optional().or(z.literal('')),
  context: z.record(z.unknown()).optional(),
  media: z.array(feedbackMediaSchema).max(5).optional(),
});
export type CreateFeedback = z.infer<typeof createFeedbackSchema>;

export const updateFeedbackSchema = z.object({
  status: z.enum(['new', 'triaged', 'in_progress', 'resolved', 'wontfix']).optional(),
  priority: z.enum(['low', 'normal', 'high', 'urgent']).optional(),
  admin_note: z.string().max(5000).optional().or(z.literal('')),
});
export type UpdateFeedback = z.infer<typeof updateFeedbackSchema>;

// === Drive config (Owner setup) ===
export const driveConfigureSchema = z.object({
  drive_root_id: z.string().min(10),     // Google Drive folder ID
});

// === รับรองเวลาเข้างาน (ขอแก้/รับรองเวลาเข้า-ออกงาน) — HR อนุมัติ ===
export const createAttendanceCorrectionSchema = z.object({
  date: dateString,
  kind: z.enum(['morning_in', 'evening_out']),
  requested_time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'เวลาต้องเป็นรูปแบบ HH:mm'),
  reason: z.string().min(1, 'ต้องระบุเหตุผล').max(300),
  // fb#313 — หลักฐานแนบ (ไม่บังคับ) path จาก POST /upload purpose=evidence
  evidence_path: z.string().max(300).optional(),
});
export type CreateAttendanceCorrection = z.infer<typeof createAttendanceCorrectionSchema>;

export const decideAttendanceCorrectionSchema = z.object({
  decision: z.enum(['approved', 'rejected']),
  rejection_reason: z.string().max(300).optional().or(z.literal('')),
  // fb#429: ขั้นที่ผู้กดเห็นบนจอ — กดซ้ำ/ส่งซ้ำหลังใบเลื่อนขั้นแล้ว จะไม่กลายเป็นการอนุมัติขั้นถัดไป
  expected_status: z.enum(['pending_supervisor', 'pending']),
});
export type DecideAttendanceCorrection = z.infer<typeof decideAttendanceCorrectionSchema>;

// === HR ลงเวลาย้อนหลังให้พนักงานเอง (พนักงานลืมสแกนและไม่ได้ยื่นขอ) ===
// ต่างจาก attendance-correction ตรงที่ HR เป็นผู้ริเริ่ม จึงบังคับเหตุผลเสมอ
// และครอบคลุมทั้ง 4 รอบ (รวมพักเที่ยง ซึ่ง flow คำขอพนักงานไม่รองรับ)
export const manualAttendanceEntrySchema = z.object({
  emp_code: z.string().min(1),
  date: dateString,
  kind: z.enum(['morning_in', 'lunch_out', 'lunch_in', 'evening_out']),
  time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'เวลาต้องเป็นรูปแบบ HH:mm'),
  reason: z.string().min(1, 'ต้องระบุเหตุผล').max(300),
});
export type ManualAttendanceEntry = z.infer<typeof manualAttendanceEntrySchema>;

// === สัญญาจ้าง / สัญญาทดลองงาน + ลงนามอิเล็กทรอนิกส์ (FM-HR-001 v3.0) ===

/** ค่าที่กรอกในส่วนที่ 1 ของฟอร์ม — ที่ไม่ได้ดึงอัตโนมัติจากทะเบียนพนักงาน */
export const contractFieldsOverrideSchema = z.object({
  emp_name_en: z.string().max(120).optional(),
  nationality: z.string().max(60).optional(),
  workplace: z.string().max(200).optional(),
  report_to: z.string().max(120).optional(),
  employee_type_label: z.string().max(60).optional(),
  renewal_term: z.string().max(200).optional(),
  pay_cycle: z.string().max(60).optional(),
  payday_text: z.string().max(60).optional(),
  pay_method: z.string().max(60).optional(),
  extra_compensation: z.string().max(500).optional(),
  /** หน้าที่ความรับผิดชอบ — ฟอร์มต้นฉบับมี 6 ช่อง */
  duties: z.array(z.string().max(300)).max(6).optional(),
});
export type ContractFieldsOverride = z.infer<typeof contractFieldsOverrideSchema>;

export const createContractSchema = z.object({
  emp_code: empCodeString,
  contract_kind: z.enum(['permanent', 'fixed_term', 'probation']),
  start_date: dateString,
  /** บังคับเมื่อ contract_kind = fixed_term (ตรวจซ้ำใน handler) */
  end_date: dateString.optional(),
  probation_days: z.number().int().min(0).max(365).optional(),
  /** ค่าจ้างต่องวด — ถ้าไม่ส่งมา handler ดึงจาก salary_master ให้ */
  salary_amount: z.number().min(0).max(100_000_000).optional(),
  salary_unit: z.string().max(40).optional(),
  fields: contractFieldsOverrideSchema.optional(),
});
export type CreateContract = z.infer<typeof createContractSchema>;

/** ส่งสัญญาให้พนักงานเซ็น (การ์ดไลน์ + ล็อกไม่ให้แก้ต่อ) */
export const sendContractSchema = z.object({
  contract_id: z.string().uuid(),
});
export type SendContract = z.infer<typeof sendContractSchema>;

/** ขอรหัส OTP เพื่อเซ็น */
export const requestContractOtpSchema = z.object({
  contract_id: z.string().uuid(),
});
export type RequestContractOtp = z.infer<typeof requestContractOtpSchema>;

/**
 * เซ็นสัญญา — ลายเซ็นเป็น data URL ของ PNG จาก canvas บนมือถือ
 *
 * จำกัดขนาดที่ ~700KB (base64 พองขึ้น ~33% จากไฟล์จริง) — ลายเซ็นวาดนิ้ว
 * ปกติไม่เกิน 50KB ค่าที่ใหญ่กว่านี้คือมีคนยัดรูปอื่นมาแทน
 */
export const signContractSchema = z.object({
  contract_id: z.string().uuid(),
  otp: z.string().regex(/^\d{6}$/, 'รหัสยืนยันต้องเป็นตัวเลข 6 หลัก'),
  /** เจตนายอมรับ — ต้องมาถึงเซิร์ฟเวอร์และถูกบันทึก ไม่ใช่ติ๊กแล้วอยู่แต่ในจอ */
  acknowledged: z.literal(true, {
    errorMap: () => ({ message: 'ต้องยืนยันว่าอ่านและเข้าใจสัญญาแล้วก่อนลงนาม' }),
  }),
  signature_data_url: z
    .string()
    .max(700_000, 'ลายเซ็นใหญ่เกินไป')
    .regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/, 'ลายเซ็นต้องเป็นภาพ PNG'),
});
export type SignContract = z.infer<typeof signContractSchema>;

/** ยกเลิกสัญญา (ออกผิด/ออกซ้ำ) — ต้องมีเหตุผลเสมอ เพราะลบไม่ได้ */
export const cancelContractSchema = z.object({
  contract_id: z.string().uuid(),
  reason: z.string().min(1, 'ต้องระบุเหตุผลที่ยกเลิก').max(300),
});
export type CancelContract = z.infer<typeof cancelContractSchema>;
